import React, { Fragment ,useContext,useState, useEffect, useRef } from 'react';
import { Link   } from 'react-router-dom';
import store from 'store'
import AuthContext from 'context/authProvider';
/*
|UI
*/
import { Divider, Skeleton, Tag, Typography } from 'antd';

/*
|ICONS
*/

import { PiGearFineDuotone, PiShoppingBagDuotone, PiCalendarCheckDuotone, PiPackageDuotone, PiBuildingsDuotone, PiBookBookmarkDuotone, PiIdentificationCardDuotone, PiGearDuotone, PiCreditCardDuotone, PiBankDuotone, PiHeadsetDuotone, PiScalesDuotone, PiNotebookDuotone, PiCalendarBlankDuotone, PiLockSimpleDuotone, PiChartBarDuotone  } from "react-icons/pi";
import { BsFillExclamationTriangleFill } from "react-icons/bs";


/*
|PAGES
*/
import IconImage from 'components/common/icon/IconImage';
import VarexeLogo from 'components/common/VarexeLogo';

// Import permission mapping



import { checkMenuPermissions, PERMISSION_MAPPING, getModulesForMenuItem, checkModuleAccess, checkFeatureAccess, getFeaturesForMenuItem } from './permissionMapping';
import { capitalize } from 'helpers/utils';

/*
| USAGE EXAMPLE:
| 
| In your React component:
| 
| import { useContext } from 'react';
| import AuthContext from 'context/authProvider';
| import getMenuData from 'services/menu';
| 
| function MyComponent() {
|   const { auth } = useContext(AuthContext);
|   const userPermissions = auth?.user?.extra?.privileges?.permissions || [];
|   
|   // Pass auth context to get dynamic menu titles
|   const menuItems = getMenuData(userPermissions, auth);
|   
|   return (
|     <Menu items={menuItems} />
|   );
| }
| 
| The menu titles will be dynamic based on organization settings:
| - sales_menu_title
| - estimates_menu_title
| - invoices_menu_title
| - customers_menu_title
| - services_menu_title
| - accounting_menu_title
| etc.
*/

// Enhanced render function with permission checking
export const renderItems = (label = '', key = '', url = '', icon = '', requiredPermissions = [], userPermissions = []) => {
  const { hasAccess, isLimited } = checkMenuPermissions(requiredPermissions, userPermissions);
  const {Text} = Typography;
  return {
    label: (
      <div className="d-flex justify-content-between align-items-center w-100">
        <Link className='text-decoration-none' to={url}>
        {isLimited && (
          <Text type='warning' className='fs--1 me-1'>
            <BsFillExclamationTriangleFill />
          </Text>
        )}        
        {label}
        </Link>
      </div>
    ),
    key: key,
    icon: icon,
    path: url, // Store the path for easy route matching
  }
}

// Enhanced group render function
export const renderGroupLabel = (label = '', requiredPermissions = [], userPermissions = []) => {
  const { hasAccess, isLimited } = checkMenuPermissions(requiredPermissions, userPermissions);
  
  return (
    <div className="d-flex justify-content-between align-items-center w-100">
      <Divider titlePlacement="start" plain className='m-0'>{label}</Divider>
      {/* {isLimited && (
        <Tag size="small" color="warning" className="ms-2">
          Limited
        </Tag>
      )} */}
    </div>
  );
}

// Skeleton loading component for menu items
export const renderSkeletonItem = () => ({
  label: <Skeleton.Input active size="small" style={{ width: 120, height: 20 }} />,
  key: `skeleton-${Math.random()}`,
  icon: <Skeleton.Avatar active size="small" shape="square" />,
});

// Create menu items with user permissions
export const createMenuItems = (userPermissions = [], authContext = null) => {
  const {Text} = Typography;




  // Get organization settings from auth context
  const organizationSettings = authContext?.settings || {};
  const userAuth = authContext?.user || {};
  const moduleAccess = userAuth?.extra?.privileges?.module_access || [];
  const licenseFeatures = userAuth?.extra?.privileges?.license_features || [];


  // Dynamic menu titles based on organization settings
  const getMenuTitle = (defaultTitle, settingKey) => {
    return organizationSettings[settingKey] || defaultTitle;
  };

  const dashboardRoutes = renderItems(
    'Dashboard',
    'dashboard',
    '/dashboard',
    <IconImage icon="dashboard"  className=''/>,
    PERMISSION_MAPPING.dashboard,
    userPermissions
  )

  const calendarRoutes = renderItems(
    'Calendar',
    'calendar',
    '/calendar',
    <PiCalendarBlankDuotone    className='fs-2 text-success'/>,
    PERMISSION_MAPPING.calendar,
    userPermissions
  )

  const salesRoutes = {
    label: (() => {
      const { hasAccess, isLimited } = checkMenuPermissions(PERMISSION_MAPPING.sales.group, userPermissions);
      
      return (
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>{getMenuTitle('Sales', 'sales_menu_title')} 
            {/* {isLimited && (
            <Text className='fs--2' type="warning">
              Limited
            </Text>
          )} */}
          </span>

        </div>
      );
    })(),
    key:   'sales',
    icon:  <PiCreditCardDuotone className='fs-2'/>,
    children: (() => {
      const items = [];
      if (checkModuleAccess(getModulesForMenuItem('sales.estimates'), moduleAccess)) {
        items.push(
          renderItems(
            getMenuTitle('Estimates', 'estimates_menu_title'),
            'estimates',
            '/estimates/list',
            null,
            PERMISSION_MAPPING.sales.estimates,
            userPermissions
          )
        );
      }

      if (checkModuleAccess(getModulesForMenuItem('sales.invoices'), moduleAccess)) {
        items.push(
          renderItems(
            getMenuTitle('Invoices', 'invoices_menu_title'),
            'invoices',
            '/invoices/list',
            null,
            PERMISSION_MAPPING.sales.invoices,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('sales.workspaces'), moduleAccess)) {
        items.push(
          renderItems(
            'Workspaces',
            'workspaces',
            '/workspaces/list',
            null,
            PERMISSION_MAPPING.sales.workspaces,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('sales.customers'), moduleAccess)) {
        items.push(
          renderItems(
            getMenuTitle('Customers', 'customers_menu_title'),
            'customers',
            '/customers/list',
            null,
            PERMISSION_MAPPING.sales.customers,
            userPermissions
          )
        );
      }
      return items;
    })(),
  }

  const servicesRoutes = {
    label: (() => {
      const { hasAccess, isLimited } = checkMenuPermissions(PERMISSION_MAPPING.services.group, userPermissions);
      return (
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>{getMenuTitle('Services & Products', 'services_menu_title')} 
            {/* {isLimited && (
            <Text className='fs--2' type="warning">
              Limited
            </Text>
          )} */}
          </span>

        </div>
      );
    })(),
    key:   'servicesproducts',
    icon:  <PiPackageDuotone className='fs-2'/>,
    children: (() => {
      const items = [];
      if (checkModuleAccess(getModulesForMenuItem('services.items'), moduleAccess)) {
        items.push(
          renderItems(
            `${capitalize(organizationSettings['format_items'])}s`,
            'services',
            '/items/list',
            null,
            PERMISSION_MAPPING.services.items,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('services.categories'), moduleAccess)) {
        items.push(
          renderItems(
            `${capitalize(organizationSettings['format_items'])} Categories`,
            'servicescategories',
            '/items/items-category/list',
            null,
            PERMISSION_MAPPING.services.categories,
            userPermissions
          )
        );
      }
      if (checkFeatureAccess(getFeaturesForMenuItem('services.configurableTemplates'), licenseFeatures)) {
        items.push(
          renderItems(
            'Configurable Templates',
            'configurable-templates',
            '/configurable-templates',
            null,
            PERMISSION_MAPPING.services.configurableTemplates,
            userPermissions
          )
        );
      }
      if (checkFeatureAccess(getFeaturesForMenuItem('services.configurableGroups'), licenseFeatures)) {
        items.push(
          renderItems(
            'Configurable Groups',
            'configurable-groups',
            '/configurable-groups',
            null,
            PERMISSION_MAPPING.services.configurableGroups,
            userPermissions
          )
        );
      }
      return items;
    })(),
  }

  const inventoryRoutes = {
    label: (() => {
      const { hasAccess, isLimited } = checkMenuPermissions(PERMISSION_MAPPING.inventory.group, userPermissions);
      
      return (
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>Inventory 
            {/* {isLimited && (
            <Text className='fs--2' type="warning">
              Limited
            </Text>
          )} */}
          </span>
        </div>
      );
    })(),
    key:   'inventory',
    icon:  <PiPackageDuotone className='fs-2'/>,
    children: (() => {
      const items = [];
      if (checkModuleAccess(getModulesForMenuItem('inventory.items'), moduleAccess)) {
        items.push(
          renderItems(
            'Inventory Items',
            'inventory-items',
            '/inventory-items/list',
            null,
            PERMISSION_MAPPING.inventory.items,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('inventory.categories'), moduleAccess)) {
        items.push(
          renderItems(
            'Inventory Categories',
            'inventory-categories',
            '/inventory/categories',
            null,
            PERMISSION_MAPPING.inventory.categories,
            userPermissions
          )
        );
      }
      return items;
    })(),
  }

  const accountingRoutes = {
    label: (() => {
      const { hasAccess, isLimited } = checkMenuPermissions(PERMISSION_MAPPING.accounting.group, userPermissions);
      
      return (
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>{getMenuTitle('Accounting', 'accounting_menu_title')} 
            {/* {isLimited && (
            <Text className='fs--2' type="warning">
              Limited
            </Text>
          )} */}
          </span>

        </div>
      );
    })(),
    key:   'accounting',
    icon:  <PiScalesDuotone className='fs-2'/>,
    children: (() => {
      const items = [];
      if (checkModuleAccess(getModulesForMenuItem('accounting.receivePayments'), moduleAccess)) {
        items.push(
          renderItems(
            'Receive Payments',
            'receivepayments',
            '/receive-payments',
            null,
            PERMISSION_MAPPING.accounting.receivePayments,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('accounting.payBills'), moduleAccess)) {
        items.push(
          renderItems(
            'Pay Bills',
            'paybills',
            '/pay-bills',
            null,
            PERMISSION_MAPPING.accounting.payBills,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('accounting.statements'), moduleAccess)) {
        items.push(
          renderItems(
            'Customers Statements',
            'customersstatements',
            '/customers-statements',
            null,
            PERMISSION_MAPPING.accounting.statements,
            userPermissions
          )
        );
      }      
      if (checkModuleAccess(getModulesForMenuItem('accounting.taxes'), moduleAccess)) {
        items.push(
          renderItems(
            'Sales Taxes',
            'salestaxes',
            '/sales-tax',
            null,
            PERMISSION_MAPPING.accounting.taxes,
            userPermissions
          )
        );
      }

      return items;
    })(),
  }

  const purchasesRoutes = {
    label: (() => {
      const { hasAccess, isLimited } = checkMenuPermissions(PERMISSION_MAPPING.purchases.group, userPermissions);
      
      return (
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>Purchases 
            {/* {isLimited && (
            <Text className='fs--2' type="warning">
              Limited
            </Text>
          )} */}
          </span>

        </div>
      );
    })(),
    key:   'purchases',
    icon:  <PiShoppingBagDuotone className='fs-2'/>,
    children: (() => {
      const items = [];
      if (checkModuleAccess(getModulesForMenuItem('purchases.suppliers'), moduleAccess)) {
        items.push(
          renderItems(
            'Suppliers',
            'suppliers',
            '/suppliers/list',
            null,
            PERMISSION_MAPPING.purchases.suppliers,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('purchases.bills'), moduleAccess)) {
        items.push(
          renderItems(
            'Bills',
            'bills',
            '/bills/list',
            null,
            PERMISSION_MAPPING.purchases.bills,
            userPermissions
          )
        );
      }
      return items;
    })(),
  }

  const myProfileRoutes =  renderItems(
    'My Profile',
    'myprofile',
    '/profile',
    <PiIdentificationCardDuotone className='fs-2'/>,
    PERMISSION_MAPPING.profile,
    userPermissions
  )  




  const todosRoutes =  renderItems(
    'Todos',
    'todos',
    '/todos',
    <PiNotebookDuotone className='fs-2'/>,
    PERMISSION_MAPPING.todos,
    userPermissions
  )  

  const myOrganizationRoutes = {
    label: (() => {
      const { hasAccess, isLimited } = checkMenuPermissions(PERMISSION_MAPPING.organization.group, userPermissions);
      
      return (
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>My Organization 
            {/* {isLimited && (
            <Text className='fs--2' type="warning">
              Limited
            </Text>
          )} */}
          </span>

        </div>
      );
    })(),
    key:   'myOrganization',
    icon:  <PiBuildingsDuotone className='fs-2'/>,
    children: (() => {
      const items = [];
      if (checkModuleAccess(getModulesForMenuItem('organization.users'), moduleAccess)) {
        items.push(
          renderItems(
            'Users',
            'users',
            '/users/list',
            null,
            PERMISSION_MAPPING.organization.users,
            userPermissions
          )
        );
      }
      if (checkModuleAccess(getModulesForMenuItem('organization.business'), moduleAccess)) {
        items.push(
          renderItems(
            'Business profile',
            'businessprofile',
            '/business-profile',
            null,
            PERMISSION_MAPPING.organization.business,
            userPermissions
          )
        );
      }

      if (checkModuleAccess(getModulesForMenuItem('organization.bankAccounts'), moduleAccess)) {
        items.push(
          renderItems(
            'Bank Accounts',
            'bankaccounts',
            '/bank-accounts',
            null,
            PERMISSION_MAPPING.organization.bankAccounts,
            userPermissions
          )
        );
      }
      return items;
    })(),
  }

  const bankAccountRoutes =  renderItems(
    'Bank Account',
    'bankaccount',
    '/admin-settings/bank-accounts',
    <PiBankDuotone className='fs-2'/>,
    PERMISSION_MAPPING.admin.bankAccounts,
    userPermissions
  )  

  const settingsRoutes =  renderItems(
    'Settings',
    'settings',
    '/settings',
    <PiGearFineDuotone className='fs-2'/>,
    PERMISSION_MAPPING.admin.settings,
    userPermissions
  )  

  const licensesRoutes =  renderItems(
    'Licenses',
    'licenses',
    '/licenses',
    <VarexeLogo type={'svgIcon'} style={{width:'20px'}}  className='fs-1'/>,
    PERMISSION_MAPPING.admin.licenses,
    userPermissions
  ) 

  const supportRoutes =  renderItems(
    'Support',
    'support',
    '/support',
    <PiHeadsetDuotone className='fs-1'/>,
    PERMISSION_MAPPING.support,
    userPermissions
  ) 


  const reportsRoutes =  renderItems(
    'Reports',
    'reports',
    '/reports',
    <PiChartBarDuotone className='fs-1'/>,
    PERMISSION_MAPPING.reports,
    userPermissions
  ) 

  return {
    dashboardRoutes,
    calendarRoutes,
    salesRoutes,
    servicesRoutes,
    inventoryRoutes,
    accountingRoutes,
    purchasesRoutes,
    myProfileRoutes,
    todosRoutes,
    myOrganizationRoutes,
    bankAccountRoutes,
    settingsRoutes,
    licensesRoutes,
    reportsRoutes,
    supportRoutes,
  };
}

// Import new menu builder for route-based menu generation
import { buildMenuFromRoutes, findMenuKeyByPath as findMenuKeyByPathNew } from './menuBuilder';

// Export the new path finder for use in NavbarLeft
export { findMenuKeyByPathNew };

export default function getMenuData(userPermissions = [], authContext = null) {
  // Use new route-based menu system for all modules
  return buildMenuFromRoutes(userPermissions, authContext);
}
